import { SubscriptionStatsDetail } from '@type/v1-api-types';
import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { Card, Meter } from '@components/common';
import { calculatePercentage } from '@utils/numbers/calculatePercentage';
import { StatisticValue } from './StatisticValue';


// Interface
// *****************************************************
interface StatisticsCardProps {
  title?: string,
  statsCategory?: SubscriptionStatsDetail,
  className?: string,
  showConsumptionMeter?: boolean,
  ctaTitle?: string,
  ctaLink?: string
  hideUsedColumn?: boolean
  hideTotalColumn?: boolean
  type?: 'job' | 'contact' | 'cv',

}


/**
 * @description Statistics card component. It shows the total, used and available values of a
 * statistics category, and a consumption meter.
 * * **title** - the title of the statistics category. Default: ''.
 * * **statsCategory** - the statistics category data. Default: undefined.
 * * **className** - additional classes for the component. Default: ''.
 *
 * @example
 * // display a statistics card
 * <StatisticsCard title="Joburi incluse" statsCategory={stats.includedJobs} />
 */
export const StatisticsCard = (props: StatisticsCardProps) => {
  // Destructure props
  const {
    title = '',
    statsCategory,
    className = '',
    showConsumptionMeter = true,
    ctaTitle = '',
    ctaLink = '',
    hideUsedColumn = false,
    hideTotalColumn = false,
    type,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Calculate consumption percentage
  const consumption = useMemo(
    () => (statsCategory?.available === 0 ? 100 : calculatePercentage(statsCategory?.used, statsCategory?.total, 2)),
    [statsCategory?.total, statsCategory?.used, statsCategory?.available],
  );

  // We have a special value for unlimited numbers
  const unLimitedNumberValue = 100000;

  let gridCols = 'grid-cols-2';

  if ((hideUsedColumn || hideTotalColumn) && statsCategory?.total !== unLimitedNumberValue) {
    gridCols = 'grid-cols-3';
  }
  // Render component
  // *****************************************************
  return (
    <Card animation="rise" className={`rounded border-b bg-surface ${className}`}>

      {/* Stats category title */}
      <Card.Header className="p-3">
        <strong>{t(title)}</strong>
      </Card.Header>

      {/* Stats category numbers. Order is: total, used, available */}
      <Card.Body className="mb-3 bg-surface px-3 py-0 text-center">
        <div className={`grid divide-x rounded bg-surface-50 py-2 ${gridCols}`}>

          {statsCategory?.total !== unLimitedNumberValue && (() => {
            const available = statsCategory?.available ?? 0;
            let value = available as string | number;

            if (type === 'job') {
              if (available > 0) {
                value = t('global.value.unlimited');
              } else {
                value = 0;
              }
            }
            return (
              <StatisticValue
                name={t('global.label.available.singular')}
                value={value}
                mainColor="text-secondary"
              />
            );
          })()}

          {!hideUsedColumn
          && <StatisticValue name={t('global.label.used-only')} value={statsCategory?.used} mainColor="text-warning" />}


          {!hideTotalColumn && (
          <StatisticValue
            name={t('global.label.total.only')}
            value={statsCategory?.total === unLimitedNumberValue ? t('global.value.unlimited') : statsCategory?.total}
            zeroColor="text-ink"
            mainColor="text-ink"
          />
          )}


        </div>
      </Card.Body>

      {showConsumptionMeter && (
        <Card.Footer className="flex items-center gap-3.5 px-3 py-3.5 text-sm">
          <div className="shrink-0 text-ink-medium">
            {t('premium.stats.consumption-percentage.label')}
          </div>
          <Meter activeWidth={consumption} activeColor="warning" />
          <div className="mt-0.5 shrink-0 font-bold">{`${consumption}%`}</div>
        </Card.Footer>
      )}


      {ctaTitle && ctaLink && (
        <Card.Footer className="pb-5 pt-3 text-center">
          <a
            href={ctaLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full rounded bg-secondary px-3.5 py-2 text-center text-sm font-medium text-white transition hover:bg-secondary-dark"
          >
            {t(ctaTitle)}
          </a>
        </Card.Footer>
      )}
    </Card>
  );
};
